import { useEffect, useRef } from "react";
import { useSelector } from "react-redux";
import { useT } from "../../i18n";

const normalizeHeading = (value) => {
  const heading = Number(value);
  if (!Number.isFinite(heading)) return 0;
  return ((heading % 360) + 360) % 360;
};

// 레이더 글로우 마커: 민트 할로 + 진행 방향 빔 + 위치점.
// 다색이 필요해 Symbol 대신 data-URI SVG 를 쓰고, heading 은 SVG 회전으로 반영한다.
// (매 틱 setIcon 으로 갱신되는 기존 구조 그대로 — data URI 라 네트워크 로드 없음)
const DRONE_MARKER_SIZE = 52;

const buildDroneMarkerIcon = (heading) => {
  const svg =
    `<svg xmlns="http://www.w3.org/2000/svg" width="${DRONE_MARKER_SIZE}" height="${DRONE_MARKER_SIZE}" viewBox="-16 -16 32 32">` +
    `<defs>` +
    `<radialGradient id="halo" cx="50%" cy="50%" r="50%">` +
    `<stop offset="0%" stop-color="rgba(55,213,164,0.35)"/>` +
    `<stop offset="70%" stop-color="rgba(55,213,164,0.12)"/>` +
    `<stop offset="100%" stop-color="rgba(55,213,164,0)"/>` +
    `</radialGradient>` +
    `<linearGradient id="beam" x1="0" y1="1" x2="0" y2="0">` +
    `<stop offset="0%" stop-color="rgba(55,213,164,0.55)"/>` +
    `<stop offset="100%" stop-color="rgba(55,213,164,0)"/>` +
    `</linearGradient>` +
    `</defs>` +
    `<g transform="rotate(${heading})">` +
    `<circle r="13" fill="url(#halo)"/>` +
    `<path d="M0 -1L-7.4 -13.5A15.5 15.5 0 0 1 7.4 -13.5Z" fill="url(#beam)"/>` +
    `<circle r="4.6" fill="#37D5A4" stroke="#0B1017" stroke-width="1.3"/>` +
    `<circle r="4.6" fill="none" stroke="rgba(255,255,255,0.85)" stroke-width="0.7"/>` +
    `<circle cy="-1.6" r="1.1" fill="#0B1017"/>` +
    `</g>` +
    `</svg>`;

  return {
    url: `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}`,
    scaledSize: new window.google.maps.Size(DRONE_MARKER_SIZE, DRONE_MARKER_SIZE),
    anchor: new window.google.maps.Point(
      DRONE_MARKER_SIZE / 2,
      DRONE_MARKER_SIZE / 2
    ),
  };
};

const DroneTracker = ({ mapRef }) => {
  const droneMarkerRef = useRef(null);
  const t = useT();

  const droneData = useSelector((state) => state.drone.droneData);
  const droneId = useSelector((state) => state.drone.selectedDrone);
  const controlPowerStatus = useSelector(
    (state) => state.drone.control?.powerStatus
  );

  useEffect(() => {
    const removeDroneMarker = () => {
      if (!droneMarkerRef.current) return;
      droneMarkerRef.current.setMap(null);
      droneMarkerRef.current = null;
    };

    if (!mapRef.current || !droneData || !window.google?.maps) return;

    const {
      latitude,
      longitude,
      altitude,
      heading,
      arming_status,
      battery_charge,
      power_status,
    } = droneData;
    const isPowered =
      typeof power_status === "boolean" ? power_status : controlPowerStatus;

    if (!isPowered) {
      removeDroneMarker();
      return;
    }

    const lat = Number(latitude);
    const lng = Number(longitude);
    if (!Number.isFinite(lat) || !Number.isFinite(lng)) return;

    // 소수점 헤딩 변화로 아이콘 URI 가 매번 달라지지 않게 정수로 반올림
    const icon = buildDroneMarkerIcon(Math.round(normalizeHeading(heading)));
    const title = t("map.marker.droneTitle", {
      id: droneId ?? "-",
      alt: altitude ?? "-",
      battery: battery_charge ?? "-",
      status: arming_status ? "ARMED" : "DISARMED",
    });
    const position = { lat, lng };

    if (!droneMarkerRef.current) {
      droneMarkerRef.current = new window.google.maps.Marker({
        map: mapRef.current,
        position,
        icon,
        title,
        clickable: false,
        zIndex: 1000,
      });
      return;
    }

    droneMarkerRef.current.setMap(mapRef.current);
    droneMarkerRef.current.setPosition(position);
    droneMarkerRef.current.setIcon(icon);
    droneMarkerRef.current.setTitle(title);
  }, [mapRef, droneData, droneId, controlPowerStatus, t]);

  useEffect(() => {
    return () => {
      if (!droneMarkerRef.current) return;
      droneMarkerRef.current.setMap(null);
      droneMarkerRef.current = null;
    };
  }, []);

  return null;
};

export default DroneTracker;
